import { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, startOfYear, subMonths, startOfQuarter, endOfQuarter, subQuarters } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export type TimeRangePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

export interface TimeRange {
  preset: TimeRangePreset;
  startDate: Date;
  endDate: Date;
}

interface TimeRangePickerProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
}

const presetLabels: Record<TimeRangePreset, string> = {
  this_month: 'This Month',
  last_month: 'Last Month',
  this_quarter: 'This Quarter',
  last_quarter: 'Last Quarter',
  this_year: 'This Year',
  last_year: 'Last Year',
  custom: 'Custom',
};

export function getPresetDates(preset: TimeRangePreset): { startDate: Date; endDate: Date } {
  const today = new Date();

  switch (preset) {
    case 'this_month':
      return {
        startDate: startOfMonth(today),
        endDate: today,
      };
    case 'last_month': {
      const lastMonth = subMonths(today, 1);
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth),
      };
    }
    case 'this_quarter':
      return {
        startDate: startOfQuarter(today),
        endDate: today,
      };
    case 'last_quarter': {
      const lastQuarter = subQuarters(today, 1);
      return {
        startDate: startOfQuarter(lastQuarter),
        endDate: endOfQuarter(lastQuarter),
      };
    }
    case 'this_year':
      return {
        startDate: startOfYear(today),
        endDate: today,
      };
    case 'last_year': {
      const lastYear = new Date(today.getFullYear() - 1, 0, 1);
      return {
        startDate: lastYear,
        endDate: new Date(today.getFullYear() - 1, 11, 31),
      };
    }
    case 'custom':
    default:
      return {
        startDate: startOfMonth(today),
        endDate: today,
      };
  }
}

export function createTimeRange(preset: TimeRangePreset, customStart?: Date, customEnd?: Date): TimeRange {
  if (preset === 'custom' && customStart && customEnd) {
    return { preset, startDate: customStart, endDate: customEnd };
  }
  const dates = getPresetDates(preset);
  return { preset, ...dates };
}

export function TimeRangePicker({ value, onChange }: TimeRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [pendingPreset, setPendingPreset] = useState<TimeRangePreset>(value.preset);
  const [pendingStart, setPendingStart] = useState<Date | undefined>(value.startDate);
  const [pendingEnd, setPendingEnd] = useState<Date | undefined>(value.endDate);

  useEffect(() => {
    setPendingPreset(value.preset);
    setPendingStart(value.startDate);
    setPendingEnd(value.endDate);
  }, [value]);

  const handlePresetChange = (preset: TimeRangePreset) => {
    setPendingPreset(preset);
    if (preset !== 'custom') {
      const dates = getPresetDates(preset);
      setPendingStart(dates.startDate);
      setPendingEnd(dates.endDate);
    }
  };

  const handleApply = () => {
    if (pendingPreset === 'custom') {
      if (pendingStart && pendingEnd) {
        onChange({ preset: 'custom', startDate: pendingStart, endDate: pendingEnd });
      }
    } else {
      onChange(createTimeRange(pendingPreset));
    }
    setOpen(false);
  };

  const getDisplayLabel = () => {
    if (value.preset === 'custom') {
      return `${format(value.startDate, 'MMM d, yyyy')} - ${format(value.endDate, 'MMM d, yyyy')}`;
    }
    return presetLabels[value.preset];
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="justify-start text-left font-normal min-w-[200px]">
          <CalendarIcon className="mr-2 h-4 w-4" />
          {getDisplayLabel()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="space-y-4">
          <RadioGroup value={pendingPreset} onValueChange={(v: string) => handlePresetChange(v as TimeRangePreset)}>
            {Object.entries(presetLabels).map(([key, label]) => (
              <div key={key} className="flex items-center space-x-2">
                <RadioGroupItem value={key} id={key} />
                <Label htmlFor={key} className="cursor-pointer">{label}</Label>
              </div>
            ))}
          </RadioGroup>

          {pendingPreset === 'custom' && (
            <div className="flex gap-4 pt-4 border-t">
              <div>
                <Label className="text-xs text-muted-foreground">Start</Label>
                <Calendar
                  mode="single"
                  selected={pendingStart}
                  onSelect={setPendingStart}
                  disabled={(date) => pendingEnd ? date > pendingEnd : false}
                  initialFocus
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">End</Label>
                <Calendar
                  mode="single"
                  selected={pendingEnd}
                  onSelect={setPendingEnd}
                  disabled={(date) => pendingStart ? date < pendingStart : false}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              disabled={pendingPreset === 'custom' && (!pendingStart || !pendingEnd)}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
